import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api';
import {
  getAdminSchedule,
  updateAdminSchedule,
  type AdminSchedule,
  type ScheduleWeek,
} from '../../lib/api/admin';
import { Modal } from './Modal';
import { isoToLocalInput, localInputToIso } from '../../lib/datetimeLocal';

// Editing is done with native datetime-local inputs in the editor's LOCAL
// timezone (US/ET-based ops → natural). The configured display timezone
// rendering is shown beside every field + in the confirm dialog as the
// source-of-truth check, so a non-ET editor still verifies the real event
// time before committing.

function fmtInTz(iso: string | null, tz: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: tz,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return new Date(iso).toUTCString();
  }
}

const previewLocal = (local: string, tz: string): string => fmtInTz(localInputToIso(local), tz);

interface DraftWeek {
  weekNumber: number;
  airAt: string;
  playCutoffAt: string;
}
interface Draft {
  config: { preEventActiveStart: string; postEventStart: string; displayTimezone: string };
  weeks: DraftWeek[];
}

function toDraft(s: AdminSchedule): Draft {
  return {
    config: {
      preEventActiveStart: isoToLocalInput(s.config.preEventActiveStart),
      postEventStart: isoToLocalInput(s.config.postEventStart),
      displayTimezone: s.config.displayTimezone,
    },
    weeks: s.weeks.map((w) => ({
      weekNumber: w.weekNumber,
      airAt: isoToLocalInput(w.airAt),
      playCutoffAt: isoToLocalInput(w.playCutoffAt),
    })),
  };
}

function buildPayload(draft: Draft): { payload?: { config: AdminSchedule['config']; weeks: ScheduleWeek[] }; error?: string } {
  const pre = localInputToIso(draft.config.preEventActiveStart);
  const post = localInputToIso(draft.config.postEventStart);
  if (!pre) return { error: 'Pre-event active start is required.' };
  if (!post) return { error: 'Post-event start is required.' };
  if (!draft.config.displayTimezone.trim()) return { error: 'Display timezone is required.' };

  const weeks: ScheduleWeek[] = [];
  for (const w of draft.weeks) {
    const airAt = localInputToIso(w.airAt);
    const playCutoffAt = localInputToIso(w.playCutoffAt);
    if (!airAt) return { error: `Week ${w.weekNumber} air time is required.` };
    if (!playCutoffAt) return { error: `Week ${w.weekNumber} play cutoff is required.` };
    weeks.push({ weekNumber: w.weekNumber, airAt, playCutoffAt });
  }
  return {
    payload: {
      config: {
        preEventActiveStart: pre,
        postEventStart: post,
        displayTimezone: draft.config.displayTimezone.trim(),
      },
      weeks,
    },
  };
}

/**
 * Schedule tab — edit event_config + event_weeks timing. Highest blast radius:
 * these timestamps decide whether the event is live. Edits are validated +
 * applied atomically server-side; saving is gated behind a confirm dialog that
 * shows the proposed schedule in the display timezone.
 */
export function ScheduleTab() {
  const [schedule, setSchedule] = useState<AdminSchedule | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [baseline, setBaseline] = useState<Draft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const applyLoaded = (s: AdminSchedule): void => {
    const d = toDraft(s);
    setSchedule(s);
    setDraft(d);
    setBaseline(d);
  };

  useEffect(() => {
    const controller = new AbortController();
    getAdminSchedule({ signal: controller.signal })
      .then((s) => applyLoaded(s))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setLoadError(
          err instanceof ApiError ? `Couldn't load the schedule (${err.status}).` : "Couldn't load the schedule.",
        );
      });
    return () => controller.abort();
  }, []);

  const dirty = useMemo(
    () => !!draft && !!baseline && JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  );

  if (loadError) return <p className="text-white/80">{loadError}</p>;
  if (!draft || !schedule) return <p className="text-white/60">Loading schedule…</p>;

  const tz = draft.config.displayTimezone.trim() || 'America/New_York';

  const setConfig = (key: keyof Draft['config'], value: string): void => {
    setSuccess(null);
    setDraft((d) => (d ? { ...d, config: { ...d.config, [key]: value } } : d));
  };
  const setWeek = (weekNumber: number, key: keyof DraftWeek, value: string): void => {
    setSuccess(null);
    setDraft((d) =>
      d ? { ...d, weeks: d.weeks.map((w) => (w.weekNumber === weekNumber ? { ...w, [key]: value } : w)) } : d,
    );
  };

  const onSaveClick = (): void => {
    const { error } = buildPayload(draft);
    if (error) {
      setSaveError(error);
      return;
    }
    setSaveError(null);
    setConfirmOpen(true);
  };

  const doSave = async (): Promise<void> => {
    const { payload, error } = buildPayload(draft);
    if (error || !payload) {
      setSaveError(error ?? 'Invalid schedule.');
      setConfirmOpen(false);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateAdminSchedule(payload);
      applyLoaded(updated);
      setConfirmOpen(false);
      setSuccess('Schedule updated. Other API instances pick up the change within ~30 seconds.');
    } catch (err) {
      setConfirmOpen(false);
      setSaveError(err instanceof ApiError ? err.message : 'Could not save the schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const st = schedule.state;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl border border-white/15 bg-black/20 p-4 text-sm">
        <span className="text-white/55">Current phase: </span>
        <span className="font-heading uppercase tracking-wide text-white">{st.phase}</span>
        <span className="text-white/55"> · Week {st.currentWeek}</span>
        {st.nextTransitionAt && (
          <span className="text-white/55"> · next change {fmtInTz(st.nextTransitionAt, tz)}</span>
        )}
        <div className="mt-1 text-[11px] text-white/40">Server time: {fmtInTz(st.serverTime, tz)}</div>
      </div>

      <p className="text-xs text-[#ffae5b]">
        These times decide whether the event is open for play. Changes are validated + applied atomically and
        take effect immediately on save (other API instances within ~30s). Review carefully before saving.
      </p>

      <section className="rounded-xl border border-white/15 bg-black/20 p-5 sm:p-6">
        <h3 className="font-heading text-sm uppercase tracking-wide text-white/70">Event window</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <DateField
            label="Pre-event active start"
            value={draft.config.preEventActiveStart}
            tz={tz}
            onChange={(v) => setConfig('preEventActiveStart', v)}
          />
          <DateField
            label="Post-event start"
            value={draft.config.postEventStart}
            tz={tz}
            onChange={(v) => setConfig('postEventStart', v)}
          />
          <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
            Display timezone
            <input
              type="text"
              value={draft.config.displayTimezone}
              onChange={(e) => setConfig('displayTimezone', e.target.value)}
              className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-white/40 focus:outline-none"
              placeholder="America/New_York"
            />
            <span className="text-[11px] normal-case tracking-normal text-white/45">IANA timezone name</span>
          </label>
        </div>
      </section>

      <section className="rounded-xl border border-white/15 bg-black/20 p-5 sm:p-6">
        <h3 className="font-heading text-sm uppercase tracking-wide text-white/70">Weeks</h3>
        <p className="mt-2 text-[11px] leading-relaxed text-white/45">
          Each week is playable from the previous week&apos;s <span className="text-white/65">play cutoff</span>{' '}
          (or the pre-event active start for Week 1) up to its own play cutoff — the derived window is shown
          under each week and updates as you edit. Cutoffs must increase week over week, and the{' '}
          <span className="text-white/65">last week&apos;s play cutoff must equal the post-event start</span>.
          &ldquo;Airs at&rdquo; is informational only and does not affect when play opens or closes.
        </p>
        <div className="mt-4 flex flex-col gap-5">
          {draft.weeks.map((w, i) => (
            <div key={w.weekNumber} className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
              <div className="mb-3 font-heading text-sm uppercase tracking-wide text-white/80">
                Week {w.weekNumber}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DateField
                  label="Airs at"
                  value={w.airAt}
                  tz={tz}
                  onChange={(v) => setWeek(w.weekNumber, 'airAt', v)}
                />
                <DateField
                  label="Play cutoff"
                  value={w.playCutoffAt}
                  tz={tz}
                  onChange={(v) => setWeek(w.weekNumber, 'playCutoffAt', v)}
                />
              </div>
              <PromotionWindow
                startLocal={i === 0 ? draft.config.preEventActiveStart : draft.weeks[i - 1]!.playCutoffAt}
                endLocal={w.playCutoffAt}
                tz={tz}
              />
            </div>
          ))}
        </div>
      </section>

      {saveError && <p className="text-sm text-[#ff8da6]">{saveError}</p>}
      {success && <p className="text-sm text-[#5bffb4]">{success}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onSaveClick}
          disabled={!dirty || saving}
          className="rounded-full bg-white/90 px-5 py-2 text-sm font-medium text-[#2a2237] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save schedule'}
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft(baseline);
            setSaveError(null);
            setSuccess(null);
          }}
          disabled={!dirty || saving}
          className="rounded-full border border-white/25 px-5 py-2 text-sm text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Reset
        </button>
        {dirty && <span className="text-xs text-white/45">Unsaved changes</span>}
      </div>

      {confirmOpen && (
        <ConfirmModal
          draft={draft}
          tz={tz}
          busy={saving}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => void doSave()}
        />
      )}
    </div>
  );
}

/** Read-only derived play window for a week: previous week's cutoff (or the
 *  pre-event active start for Week 1) → own cutoff. Computed from the DRAFT
 *  values so it moves live while the admin edits, before saving. */
function PromotionWindow({ startLocal, endLocal, tz }: { startLocal: string; endLocal: string; tz: string }) {
  return (
    <p className="mt-3 border-t border-white/10 pt-2 text-[11px] text-white/45">
      <span className="uppercase tracking-wide text-white/55">Promotion window</span>{' '}
      <span className="text-white/70">
        {startLocal ? previewLocal(startLocal, tz) : '—'} <span className="text-white/40">→</span>{' '}
        {endLocal ? previewLocal(endLocal, tz) : '—'}
      </span>
    </p>
  );
}

function DateField({
  label,
  value,
  tz,
  onChange,
}: {
  label: string;
  value: string;
  tz: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
      <span>{label}</span>
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-white/40 focus:outline-none [color-scheme:dark]"
      />
      <span className="text-[11px] normal-case tracking-normal text-white/45">
        {value ? previewLocal(value, tz) : '—'}
      </span>
    </label>
  );
}

function ConfirmModal({
  draft,
  tz,
  busy,
  onCancel,
  onConfirm,
}: {
  draft: Draft;
  tz: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal title="Apply schedule change?" onClose={onCancel} disabled={busy} maxWidthClass="max-w-[560px]">
      <p className="mt-3 text-sm text-[#ffae5b]">
        This updates the live event timing and can change whether the event is currently open for play.
        Times shown in {tz}.
      </p>

      <dl className="mt-4 space-y-1 text-sm text-white/85">
        <Row label="Pre-event active" value={previewLocal(draft.config.preEventActiveStart, tz)} />
        <Row label="Post-event start" value={previewLocal(draft.config.postEventStart, tz)} />
      </dl>

      <div className="mt-4 space-y-3">
        {draft.weeks.map((w, i) => (
          <div key={w.weekNumber} className="rounded-md bg-white/5 p-3 text-sm">
            <div className="font-heading text-xs uppercase tracking-wide text-white/70">Week {w.weekNumber}</div>
            <dl className="mt-1 space-y-0.5 text-white/80">
              <Row label="Airs" value={previewLocal(w.airAt, tz)} />
              <Row label="Play cutoff" value={previewLocal(w.playCutoffAt, tz)} />
              <Row
                label="Promotion window"
                value={`${previewLocal(
                  i === 0 ? draft.config.preEventActiveStart : draft.weeks[i - 1]!.playCutoffAt,
                  tz,
                )} → ${previewLocal(w.playCutoffAt, tz)}`}
              />
            </dl>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-full border border-white/25 px-5 py-2 text-sm text-white transition hover:bg-white/10 disabled:opacity-50 sm:min-w-[120px]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="rounded-full bg-white/90 px-5 py-2 text-sm font-medium text-[#2a2237] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[180px]"
        >
          {busy ? 'Saving…' : 'Apply schedule'}
        </button>
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-white/55">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
